import React, { FC } from 'react';
import { View } from 'react-native';
import i18n from 'i18n-js';
import { Gauge } from '@components/common/CircleBar';
import { GaugeRowProps } from '@components/wallboard/types';
import { WALLBOARD_GAUGE_CONFIG } from '@components/wallboard/constants';
import { getOEEColor, getPiecesFontSize, getPlaceholderFontSize } from '@components/wallboard/helpers';
import useManageGaugeRow from '@components/wallboard/hooks/useManageGaugeRow';
import { isWeb } from '@helpers';

const tourDataSet = (id?: string) => (isWeb && id ? { tourId: id } : undefined);

const GaugeRow: FC<GaugeRowProps> = ({
  batch,
  oeeValue,
  firstScheduledJob,
  ncFileStatus,
  isWithERP,
  scale,
  font,
  C,
  tourIds,
  onSchedulePlaceholderPress,
}) => {
  const gaugeSize = scale(WALLBOARD_GAUGE_CONFIG.GAUGE_SIZE);
  const gaugeProps = { size: gaugeSize, scale, font, C };

  const {
    quantityReady,
    quantityTarget,
    hasTarget,
    displayReady,
    displayTarget,
    showFraction,
    hasOEE,
    oeeRounded,
    daysDiff,
    isBehind,
    hasScheduleData,
    scheduleNotLinkedText,
  } = useManageGaugeRow({ batch, oeeValue, firstScheduledJob, ncFileStatus });

  return (
    <View style={{ flexDirection: 'row', gap: scale(10) }}>
      <Gauge
        {...gaugeProps}
        label={i18n.t('Pieces')}
        value={String(displayReady)}
        secondaryValue={showFraction ? String(displayTarget) : undefined}
        progress={hasTarget ? Math.min(quantityReady / quantityTarget!, 1) : 0}
        arcColor={C('Wallboard/Light/Gauge Pieces')}
        valueFontSize={getPiecesFontSize(quantityReady, quantityTarget, showFraction, scale)}
        dataSet={tourDataSet(tourIds?.piecesProgress)}
      />
      <Gauge
        {...gaugeProps}
        label={i18n.t('OEE Today')}
        value={hasOEE ? String(oeeRounded) : '--'}
        suffix={hasOEE ? '%' : undefined}
        progress={hasOEE ? oeeRounded / 100 : 0}
        arcColor={hasOEE ? getOEEColor(oeeRounded, C) : C('Background/Light/Chart')}
        valueColor={hasOEE ? getOEEColor(oeeRounded, C) : undefined}
        noData={!hasOEE}
        dataSet={tourDataSet(tourIds?.oeeGauge)}
      />
      {isWithERP && (
        <Gauge
          {...gaugeProps}
          label={isBehind ? i18n.t('Days Behind') : i18n.t('Days Ahead')}
          value={isBehind ? `-${daysDiff}` : String(daysDiff)}
          progress={Math.min(daysDiff / WALLBOARD_GAUGE_CONFIG.MAX_SCHEDULE_DAYS, 1)}
          counterClockwise={isBehind}
          arcColor={isBehind ? C('Wallboard/Light/Gauge OEE Poor') : C('System/Light/Blue')}
          valueColor={isBehind ? C('Wallboard/Light/Gauge OEE Poor') : C('System/Light/Blue')}
          isPlaceholder={!hasScheduleData}
          noDataText={scheduleNotLinkedText}
          noDataFontSize={scheduleNotLinkedText ? getPlaceholderFontSize(scheduleNotLinkedText, scale) : undefined}
          onPress={!hasScheduleData ? onSchedulePlaceholderPress : undefined}
          dataSet={tourDataSet(tourIds?.scheduleGauge)}
        />
      )}
    </View>
  );
};

export default GaugeRow;
